import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  ImageList,
  ImageListItem
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getProductById } from '../services/productService';

function ProductDetail({ addToCart }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [mainImage, setMainImage] = useState('');

  useEffect(() => {
    fetchProductDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProductDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      const foundProduct = await getProductById(id);

      if (!foundProduct) {
        setError('Prodotto non trovato');
        return;
      }

      setProduct(foundProduct);
      setMainImage(foundProduct.imageUrl || (foundProduct.images && foundProduct.images[0]) || '');
    } catch (err) {
      setError('Errore nel caricamento del prodotto');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product.sizes?.length > 0 && !selectedSize) {
      alert('Seleziona una taglia');
      return;
    }
    if (product.colors?.length > 0 && !selectedColor) {
      alert('Seleziona un colore');
      return;
    }

    const productToAdd = {
      ...product,
      selectedSize,
      selectedColor,
      cartId: `${product.id}-${selectedSize}-${selectedColor}-${Date.now()}`
    };

    addToCart(productToAdd);
    alert('Prodotto aggiunto al carrello!');
  };

  const handleProvaMi = () => {
    localStorage.setItem('currentProduct', JSON.stringify(product));
    navigate('/photo-capture');
  };

  const calculateDiscount = () => {
    if (!product.originalPrice || product.originalPrice <= product.price) return 0;
    return Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Prodotto non trovato'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          component={Link}
          to="/products"
        >
          Torna ai prodotti
        </Button>
      </Box>
    );
  }

  const allImages = product.images && product.images.length > 0
    ? product.images
    : product.imageUrl ? [product.imageUrl] : [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        component={Link}
        to="/products"
        sx={{ mb: 2 }}
      >
        Torna ai prodotti
      </Button>

      <Grid container spacing={4}>
        {/* Immagini prodotto */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
              {mainImage ? (
                <img
                  src={mainImage}
                  alt={product.name}
                  style={{ width: '100%', height: 'auto', borderRadius: 8, maxHeight: 500, objectFit: 'contain' }}
                />
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography color="text.secondary">Nessuna immagine</Typography>
                </Box>
              )}
            </Box>

            {allImages.length > 1 && (
              <ImageList cols={4} gap={8}>
                {allImages.map((image, index) => (
                  <ImageListItem
                    key={index}
                    sx={{
                      cursor: 'pointer',
                      border: mainImage === image ? '2px solid #667eea' : 'none',
                      borderRadius: 1
                    }}
                    onClick={() => setMainImage(image)}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      loading="lazy"
                      style={{ borderRadius: 4 }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Paper>
        </Grid>

        {/* Dettagli prodotto */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            {/* Tags */}
            {product.tags?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {product.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                    color="default"
                  />
                ))}
              </Box>
            )}

            {/* Nome e categoria */}
            <Typography variant="overline" color="text.secondary">
              {[product.brand, product.category].filter(Boolean).join(' - ')}
            </Typography>
            <Typography variant="h4" gutterBottom>
              {product.name}
            </Typography>

            {/* Prezzo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography variant="h4" color="primary">
                {product.price != null ? `€${product.price}` : 'N/D'}
              </Typography>
              {product.originalPrice > product.price && (
                <>
                  <Typography
                    variant="h6"
                    sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                  >
                    €{product.originalPrice}
                  </Typography>
                  <Chip
                    label={`-${calculateDiscount()}%`}
                    color="error"
                    size="small"
                  />
                </>
              )}
            </Box>

            {/* Descrizione */}
            {product.description && (
              <Typography variant="body1" paragraph>
                {product.description}
              </Typography>
            )}

            {/* Barcode */}
            {product.barcode && (
              <Chip
                label={`Barcode: ${product.barcode}`}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
              />
            )}

            {/* Selezione colore */}
            {product.colors?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Colore: {selectedColor || 'Seleziona'}
                </Typography>
                <ToggleButtonGroup
                  value={selectedColor}
                  exclusive
                  onChange={(e, newColor) => setSelectedColor(newColor)}
                >
                  {product.colors.map((color) => (
                    <ToggleButton key={color} value={color}>
                      {color}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Selezione taglia */}
            {product.sizes?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Taglia: {selectedSize || 'Seleziona'}
                </Typography>
                <ToggleButtonGroup
                  value={selectedSize}
                  exclusive
                  onChange={(e, newSize) => setSelectedSize(newSize)}
                >
                  {product.sizes.map((size) => (
                    <ToggleButton key={size} value={size}>
                      {size}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Bottoni azione */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleProvaMi}
                fullWidth
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontWeight: 700,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                  }
                }}
              >
                ProvaMi
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<AddShoppingCartIcon />}
                onClick={handleAddToCart}
                fullWidth
              >
                Aggiungi al Carrello
              </Button>
            </Box>

            {/* Dettagli prodotto */}
            {product.details && (
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  Dettagli Prodotto
                </Typography>
                {Object.entries(product.details).map(([key, value]) => (
                  <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {key.replace(/_/g, ' ')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ProductDetail;
